import { ArrowNarrowRightIcon, ChevronRightIcon, ClockIcon } from '@heroicons/react/outline';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { ReactNode } from 'react';

export const TARGET_DATE_FORMAT = 'YYYY-MM';

export type DurationStat = { averageMinutes: number; sampleSize: number };

export const ACCENTS = {
  teal: { badge: 'bg-customPrimary/10 text-customPrimary', dot: 'bg-customPrimary' },
  gold: { badge: 'bg-customGold/10 text-customGold', dot: 'bg-customGold' },
  lila: { badge: 'bg-customLila/10 text-customLila', dot: 'bg-customLila' },
  green: { badge: 'bg-customGreen/10 text-customGreen', dot: 'bg-customGreen' },
} as const;

export type AccentKey = keyof typeof ACCENTS;

export const formatLeadTime = (minutes: number) => {
  const roundedMinutes = Math.max(0, Math.round(minutes));
  const days = Math.floor(roundedMinutes / (60 * 24));
  const hours = Math.floor((roundedMinutes % (60 * 24)) / 60);
  const remainderMinutes = roundedMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${remainderMinutes}m`;
  }

  return `${remainderMinutes}m`;
};

export const formatSeconds = (seconds?: number) => formatLeadTime((seconds ?? 0) / 60);

export const formatSignedSeconds = (seconds?: number) => {
  const value = seconds ?? 0;
  const magnitude = formatLeadTime(Math.abs(value) / 60);

  if (Math.round(Math.abs(value) / 60) === 0) return magnitude;

  return `${value < 0 ? '-' : '+'}${magnitude}`;
};

export const formatDuration = (stat?: DurationStat) => {
  if (!stat || stat.sampleSize === 0) return 'n/a';
  return formatLeadTime(stat.averageMinutes);
};

export const formatWeekLabel = (weekStart: string, weekEnd: string) => {
  const start = moment(weekStart);
  const endInclusive = moment(weekEnd).subtract(1, 'day');

  if (start.isSame(endInclusive, 'month')) {
    return `${start.format('D')}–${endInclusive.format('D MMM')}`;
  }

  return `${start.format('D MMM')}–${endInclusive.format('D MMM')}`;
};

export const formatEur = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(Math.max(0, amount || 0));

export const MetricCard = (props: {
  label: string;
  value: ReactNode;
  icon: ReactNode;
  accent: AccentKey;
  onClick?: () => void;
}) => {
  const { label, value, icon, accent, onClick } = props;

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick();
            }
          : undefined
      }
      className={mergeClassNames(
        'flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5 transition-colors',
        onClick ? 'cursor-pointer hover:bg-surfaceHover' : ''
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className={mergeClassNames(
            'flex h-10 w-10 items-center justify-center rounded-xl',
            ACCENTS[accent].badge
          )}
        >
          {icon}
        </span>
        {onClick ? <ChevronRightIcon className="h-5 w-5 text-customGrey" /> : null}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-3xl font-bold leading-none text-white">{value}</span>
        <span className="text-sm text-customGrey">{label}</span>
      </div>
    </div>
  );
};

export const DurationCard = (props: { from: string; to: string; stat?: DurationStat }) => {
  const { from, to, stat } = props;
  const hasData = !!stat && stat.sampleSize > 0;

  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
      <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
        <ClockIcon className="h-4 w-4 shrink-0 text-customGrey" />
        <span className="text-white">{from}</span>
        <ArrowNarrowRightIcon className="h-4 w-4 shrink-0 text-customGrey" />
        <span className="text-white">{to}</span>
      </div>
      <span className="text-3xl font-bold leading-none text-white">{formatDuration(stat)}</span>
      <span className="text-xs text-customGrey">
        {hasData
          ? `Based on ${stat.sampleSize} ${stat.sampleSize === 1 ? 'record' : 'records'} this month`
          : 'No data this month'}
      </span>
    </div>
  );
};

export const ColumnHeader = (props: { dot?: AccentKey; children: ReactNode }) => (
  <th className="px-5 py-3 text-right font-semibold">
    <span className="inline-flex items-center justify-end gap-1.5">
      {props.dot ? (
        <span className={mergeClassNames('h-2 w-2 rounded-full', ACCENTS[props.dot].dot)} />
      ) : null}
      {props.children}
    </span>
  </th>
);

export const SectionHeading = (props: { title: string; description?: string }) => (
  <div className="flex flex-col gap-1">
    <h2 className="text-lg font-semibold">{props.title}</h2>
    {props.description ? <p className="text-sm text-customGrey">{props.description}</p> : null}
  </div>
);

export const EmptyState = (props: { children: ReactNode }) => (
  <div className="rounded-2xl bg-surfaceObject p-8 text-center text-sm text-customGrey">
    {props.children}
  </div>
);

export const TableShell = (props: { children: ReactNode; minWidth?: string }) => (
  <div className="overflow-x-auto rounded-2xl bg-surfaceObject">
    <table className={mergeClassNames('w-full text-sm', props.minWidth ?? 'min-w-[640px]')}>
      {props.children}
    </table>
  </div>
);

export const FilterField = (props: { width: string; children: ReactNode }) => (
  <div className={mergeClassNames('w-full', props.width)}>{props.children}</div>
);
